import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Subcategorías ────────────────────────────────────────────────────────────
const SUBCATS = [
  { key: "riego",    label: "Riego",         color: "#16a34a", bg: "#eafbf1", fg: "#0f5c2c" },
  { key: "camellon", label: "Camellón",       color: "#65a30d", bg: "#f0fdf4", fg: "#3a5c05" },
  { key: "tinaco_potable", label: "Tinaco Potable", color: "#2563eb", bg: "#eef4ff", fg: "#1e40af" },
  { key: "tinaco_tratada", label: "Tinaco Tratada", color: "#16a34a", bg: "#f0fdf4", fg: "#14532d" },
  { key: "pileta",   label: "Pileta",         color: "#a16207", bg: "#fefce8", fg: "#713f12" },
  { key: "garza",    label: "Llenado en Garza", color: "#ea580c", bg: "#fff7ed", fg: "#9a3412" },
  { key: "fuente",   label: "Fuente",         color: "#d97706", bg: "#fff7e8", fg: "#92400e" },
  { key: "bomberos", label: "Bomberos",       color: "#ca8a04", bg: "#fefce8", fg: "#78350f" },
  { key: "crc",      label: "CRC / Oficio",   color: "#dc2626", bg: "#fdecec", fg: "#8f1c1c" },
  { key: "extra",    label: "Extraordinario", color: "#db2777", bg: "#fdf2f8", fg: "#831843" },
];
const SUBCAT_MAP = Object.fromEntries(SUBCATS.map((s) => [s.key, s]));
const ALL_ON = Object.fromEntries(SUBCATS.map((s) => [s.key, true]));

// Grupos de métricas (3 tarjetas de conteo)
const GRUPOS = {
  ordinario: ["riego", "camellon", "tinaco_potable", "tinaco_tratada", "pileta", "garza", "fuente", "bomberos"],
  crc:   ["crc"],
  extra: ["extra"],
};

function hoyChihuahua() {
  const now = new Date(Date.now() - 6 * 60 * 60 * 1000);
  return now.toISOString().slice(0, 10);
}
function primerDiaMes() {
  return hoyChihuahua().slice(0, 8) + "01";
}

// Parsea variantes de coordenadas
function parseCoordenadas(texto) {
  const t = texto.trim();
  let lat, lng;
  const m1 = t.match(/^(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)$/);
  if (m1) { lat = parseFloat(m1[1]); lng = parseFloat(m1[2]); }
  if (lat == null) {
    const m2 = t.match(/^(\d+\.\d+)(-\d+(?:\.\d+)?)$/);
    if (m2) { lat = parseFloat(m2[1]); lng = parseFloat(m2[2]); }
  }
  if (lat == null) {
    const m3 = t.match(/^(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)$/);
    if (m3) { lat = parseFloat(m3[1]); lng = -parseFloat(m3[2]); }
  }
  if (lat == null || isNaN(lat) || isNaN(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return [lat, lng];
}

function FlyTo({ target, zoom }) {
  const map = useMap();
  useEffect(() => { if (target) map.flyTo(target, zoom ?? 15); }, [target, zoom, map]);
  return null;
}

export default function MapaIntervenciones() {
  const [searchParams] = useSearchParams();

  const [desde, setDesde] = useState(searchParams.get("desde") || primerDiaMes());
  const [hasta, setHasta]   = useState(searchParams.get("hasta") || hoyChihuahua());
  const [pipaFiltro, setPipaFiltro] = useState(searchParams.get("pipa") || "");
  const [subcatsOn, setSubcatsOn] = useState({ ...ALL_ON });
  const [busqueda, setBusqueda] = useState("");

  const [datos, setDatos] = useState([]);
  const [metricas, setMetricas] = useState(null);
  const [pipasDisponibles, setPipasDisponibles] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [flyZoom, setFlyZoom] = useState(15);
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(window.innerWidth >= 820);

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const params = new URLSearchParams({ desde, hasta });
      if (pipaFiltro) params.set("pipa", pipaFiltro);
      const res = await fetch(`${API}/publico/mapa-intervenciones?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const evs = json.eventos || [];
      setDatos(evs);
      setMetricas(json.metricas || null);
      const ps = [...new Set(evs.map((e) => e.pipa).filter(Boolean))].sort(
        (a, b) => Number(a) - Number(b)
      );
      setPipasDisponibles(ps);
    } catch {
      setError("No se pudo cargar el mapa. Intenta de nuevo.");
    } finally {
      setCargando(false);
    }
  }, [desde, hasta, pipaFiltro]);

  useEffect(() => { cargarDatos(); }, []); // carga inicial

  const coordsBusqueda = busqueda.trim() ? parseCoordenadas(busqueda) : null;

  const filtrados = datos.filter((d) => {
    if (!subcatsOn[d.subcategoria]) return false;
    if (busqueda.trim() && !coordsBusqueda) {
      const q = busqueda.trim().toLowerCase();
      if (!(d.lugar || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // Métricas de resumen basadas en datos totales (no sólo filtrados por chip)
  const totalPipas = metricas?.pipas ?? new Set(filtrados.map((d) => d.pipa).filter(Boolean)).size;
  const totalOrdinario = GRUPOS.ordinario.reduce((s, k) => s + (metricas?.por_subcat?.[k] ?? 0), 0);
  const totalCrc   = metricas?.crc   ?? 0;
  const totalExtra = metricas?.extra ?? 0;

  return (
    <>
      <style>{`
        :root {
          --azul:#2563eb; --texto:#1c2430; --texto-sec:#5b6472;
          --linea:#e6e9ee; --fondo:#f7f8fa;
        }
        .mp *{box-sizing:border-box;}
        .mp{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;background:var(--fondo);color:var(--texto);min-height:100vh;}
        .mp-header{padding:16px 28px;background:#fff;border-bottom:1px solid var(--linea);display:flex;align-items:center;justify-content:space-between;gap:12px;}
        .mp-header-text h1{margin:0;font-size:21px;font-weight:700;}
        .mp-header-text p{margin:4px 0 0;color:var(--texto-sec);font-size:13.5px;}
        .mp-toggle-filtros{display:none;align-items:center;gap:5px;background:var(--azul);color:#fff;border:none;border-radius:8px;padding:8px 14px;font-size:13px;font-weight:600;cursor:pointer;white-space:nowrap;flex-shrink:0;}
        @media (max-width:819px){.mp-toggle-filtros{display:flex;}}
        .mp-filtros{display:flex;gap:12px;flex-wrap:wrap;align-items:flex-end;padding:14px 28px;background:#fff;border-bottom:1px solid var(--linea);}
        .mp-campo{display:flex;flex-direction:column;gap:4px;}
        .mp-campo label{font-size:10.5px;font-weight:700;color:var(--texto-sec);text-transform:uppercase;letter-spacing:.04em;}
        .mp-campo input,.mp-campo select{border:1px solid var(--linea);border-radius:8px;padding:7px 10px;font-size:13px;background:#fbfbfc;min-width:130px;}
        .mp-chips{display:flex;gap:6px;flex-wrap:wrap;}
        .mp-chip{display:inline-flex;align-items:center;gap:5px;border:1.5px solid var(--linea);border-radius:999px;padding:5px 11px;font-size:12px;cursor:pointer;background:#fbfbfc;user-select:none;transition:all .12s;}
        .mp-chip input{margin:0;width:12px;height:12px;accent-color:currentColor;}
        .mp-btn{margin-left:auto;background:var(--azul);color:#fff;border:none;border-radius:8px;padding:8px 17px;font-size:13px;font-weight:600;cursor:pointer;white-space:nowrap;}
        .mp-btn:disabled{opacity:.6;cursor:default;}
        .mp-resumen{display:grid;grid-template-columns:repeat(5,1fr);gap:12px;padding:14px 28px;}
        .mp-tarjeta{background:#fff;border:1px solid var(--linea);border-radius:12px;padding:13px 15px;}
        .mp-tarjeta .num{font-size:22px;font-weight:800;}
        .mp-tarjeta .lbl{font-size:11px;color:var(--texto-sec);text-transform:uppercase;letter-spacing:.03em;margin-top:2px;}
        .mp-tarjeta.t-pipas .num{color:var(--azul);}
        .mp-tarjeta.t-ord   .num{color:#16a34a;}
        .mp-tarjeta.t-crc   .num{color:#dc2626;}
        .mp-tarjeta.t-extra .num{color:#db2777;}
        .mp-cuerpo{display:grid;grid-template-columns:1fr 340px;gap:0;height:calc(100vh - 310px);min-height:460px;}
        .mp-lista{overflow-y:auto;border-left:1px solid var(--linea);background:#fff;}
        .mp-lista h3{margin:0;padding:12px 15px 7px;font-size:12px;color:var(--texto-sec);text-transform:uppercase;letter-spacing:.04em;position:sticky;top:0;background:#fff;z-index:1;}
        .mp-item{padding:11px 15px;border-bottom:1px solid var(--linea);cursor:pointer;}
        .mp-item:hover{background:#f8f9fb;}
        .mp-badge{display:inline-block;font-size:10px;font-weight:700;padding:2px 7px;border-radius:999px;margin-bottom:4px;}
        .mp-lugar{font-weight:600;font-size:13px;}
        .mp-meta{font-size:11.5px;color:var(--texto-sec);margin-top:2px;}
        .mp-error{padding:12px 28px;color:#dc2626;font-size:13.5px;}
        .mp-popup{font-size:13px;line-height:1.6;min-width:190px;}
        .mp-popup b{display:block;font-size:13.5px;margin-bottom:2px;}
        .pp-badge{display:inline-block;font-size:10px;font-weight:700;padding:1px 7px;border-radius:999px;margin-bottom:4px;}
        @media (max-width:820px){
          .mp-cuerpo{grid-template-columns:1fr;height:auto;}
          .mp-mapa-wrap{height:420px;}
          .mp-lista{border-left:none;border-top:1px solid var(--linea);max-height:360px;}
          .mp-resumen{grid-template-columns:repeat(2,1fr);}
        }
      `}</style>

      <div className="mp">
        <div className="mp-header">
          <div className="mp-header-text">
            <h1>🗺️ Mapa de Intervenciones — DMU614</h1>
            <p>{cargando ? "Cargando datos…" : `${filtrados.length} intervenciones con GPS`}</p>
          </div>
          <button
            className="mp-toggle-filtros"
            onClick={() => setFiltrosAbiertos((v) => !v)}
            aria-expanded={filtrosAbiertos}
          >
            {filtrosAbiertos ? "▲ Filtros" : "▼ Filtros"}
          </button>
        </div>

        {filtrosAbiertos && <div className="mp-filtros">
          <div className="mp-campo">
            <label>Desde</label>
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
          </div>
          <div className="mp-campo">
            <label>Hasta</label>
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
          </div>
          <div className="mp-campo">
            <label>Pipa</label>
            <select value={pipaFiltro} onChange={(e) => setPipaFiltro(e.target.value)}>
              <option value="">Todas</option>
              {pipasDisponibles.map((p) => (
                <option key={p} value={p}>Pipa #{p}</option>
              ))}
            </select>
          </div>
          <div className="mp-campo" style={{ flexGrow: 1, minWidth: 200 }}>
            <label>Buscar lugar o coordenadas</label>
            <input
              type="text"
              placeholder='ej: "Parque 700"  ó  28.638,-106.089'
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
          <div className="mp-campo" style={{ flexBasis: "100%" }}>
            <label>Subcategoría</label>
            <div className="mp-chips">
              {SUBCATS.map(({ key, label, color, bg, fg }) => {
                const on = subcatsOn[key];
                return (
                  <label
                    key={key}
                    className="mp-chip"
                    style={on ? { background: bg, borderColor: color, color: fg } : {}}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={(e) =>
                        setSubcatsOn((prev) => ({ ...prev, [key]: e.target.checked }))
                      }
                    />
                    <span
                      style={{
                        display: "inline-block",
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        background: color,
                        flexShrink: 0,
                      }}
                    />
                    {label}
                  </label>
                );
              })}
            </div>
          </div>
          <button className="mp-btn" onClick={cargarDatos} disabled={cargando}>
            {cargando ? "Cargando…" : "Aplicar filtros"}
          </button>
        </div>}

        {error && <div className="mp-error">⚠️ {error}</div>}

        <div className="mp-resumen">
          <div className="mp-tarjeta t-pipas">
            <div className="num">{totalPipas}</div>
            <div className="lbl">Pipas involucradas</div>
          </div>
          <div className="mp-tarjeta">
            <div className="num">{(metricas?.total ?? datos.length)}</div>
            <div className="lbl">Intervenciones totales</div>
          </div>
          <div className="mp-tarjeta t-ord">
            <div className="num">{totalOrdinario}</div>
            <div className="lbl">Ordinarios</div>
          </div>
          <div className="mp-tarjeta t-crc">
            <div className="num">{totalCrc}</div>
            <div className="lbl">CRC / Oficios</div>
          </div>
          <div className="mp-tarjeta t-extra">
            <div className="num">{totalExtra}</div>
            <div className="lbl">Extraordinarios</div>
          </div>
        </div>

        <div className="mp-cuerpo">
          <div className="mp-mapa-wrap" style={{ width: "100%", height: "100%" }}>
            <MapContainer
              center={[28.638, -106.089]}
              zoom={12}
              style={{ width: "100%", height: "100%" }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="© OpenStreetMap"
              />
              <FlyTo target={flyTarget} zoom={flyZoom} />
              <FlyTo target={coordsBusqueda} zoom={16} />
              {filtrados.map((d, i) => {
                const sc = SUBCAT_MAP[d.subcategoria] || SUBCAT_MAP.riego;
                return (
                  <CircleMarker
                    key={d.id || i}
                    center={[d.lat, d.lng]}
                    radius={8}
                    pathOptions={{
                      color: "#fff",
                      weight: 2,
                      fillColor: sc.color,
                      fillOpacity: 1,
                    }}
                  >
                    <Popup>
                      <div className="mp-popup">
                        <span
                          className="pp-badge"
                          style={{ background: sc.bg, color: sc.fg }}
                        >
                          {sc.label}
                        </span>
                        <b>{d.lugar}</b>
                        Pipa #{d.pipa} · {d.chofer}<br />
                        Tipo: {d.tipo}<br />
                        Fecha: {d.fecha}{d.hora ? ` ${d.hora}` : ""}<br />
                        Veces atendido: {d.veces}
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          </div>

          <div className="mp-lista">
            <h3>Intervenciones ({filtrados.length})</h3>
            {filtrados.map((d, i) => {
              const sc = SUBCAT_MAP[d.subcategoria] || SUBCAT_MAP.riego;
              return (
                <div
                  key={d.id || i}
                  className="mp-item"
                  onClick={() => { setFlyZoom(15); setFlyTarget([d.lat, d.lng]); }}
                >
                  <span
                    className="mp-badge"
                    style={{ background: sc.bg, color: sc.fg, border: `1px solid ${sc.color}` }}
                  >
                    {sc.label}
                  </span>
                  <div className="mp-lugar">{d.lugar}</div>
                  <div className="mp-meta">
                    Pipa #{d.pipa} · {d.chofer} · {d.fecha}
                    {d.hora ? ` ${d.hora}` : ""} · {d.veces}x atendido
                  </div>
                </div>
              );
            })}
            {filtrados.length === 0 && !cargando && (
              <div style={{ padding: "22px 15px", color: "#5b6472", fontSize: 13 }}>
                Sin intervenciones para los filtros seleccionados.
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
